// src/components/QuickCreate.tsx
// The global create menu (⌘N, and the sidebar's create button).
//
// "Create a task" means something different depending on where you are. On the
// task list it means the quick drawer beside the list — the list is what makes
// the parent obvious and stops duplicates, so leaving it would be a downgrade.
// Anywhere else there is no list to stay on, and the full page is the answer.
// The menu does not guess from the URL: it asks whether a task list is on
// screen (see quickCreateBus) and falls back to navigating when none is.

import { useNavigate } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { requestTaskQuickCreate } from '@/features/task/form/quickCreateBus';

interface QuickCreateProps {
  onClose: () => void;
}

interface CreateItem {
  labelKey: string;
  href: string;
  icon: string;
  /** Runs first; returning true means it handled the request in place. */
  inPlace?: () => boolean;
}

const CREATE_ITEMS: CreateItem[] = [
  {
    labelKey: 'layout.quickCreate.task',
    href: '/tasks/new',
    icon: 'TA',
    inPlace: requestTaskQuickCreate,
  },
  { labelKey: 'layout.quickCreate.document', href: '/documents/new', icon: 'DO' },
  { labelKey: 'layout.quickCreate.project', href: '/projects/new', icon: 'PR' },
  { labelKey: 'layout.quickCreate.event', href: '/calendar', icon: 'CA' },
];

export function QuickCreate({ onClose }: QuickCreateProps) {
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleSelect = (item: CreateItem) => {
    if (!item.inPlace?.()) navigate(item.href);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div
        className="absolute top-14 left-20 md:left-60 w-56 bg-card rounded-lg border border-border shadow-xl"
        role="menu"
        aria-label={t('layout.sidebar.quickCreate')}
      >
        <div className="p-1">
          {CREATE_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => handleSelect(item)}
              role="menuitem"
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
            >
              <span className="h-5 w-5 flex items-center justify-center text-[10px] font-bold text-muted-foreground">
                {item.icon}
              </span>
              {t(item.labelKey)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
